import type { ErrorInfo, ReactNode } from "react";
import { Component } from "react";
import { captureException } from "@freightsimple/observability/errorReporting";
import {
  parseStackFrames,
  type StackFrame,
} from "@freightsimple/ui-core/Feedback/parseStackFrames";
import { StackFrameList } from "@freightsimple/ui-core/Feedback/StackFrameList";
import { isProduction } from "@/Environment";

/**
 * Fires a `dashboard:open-file` event the dashboard's Component Inspector
 * listens for. Kept lightweight so the boundary doesn't need to import the
 * inspector (which would cycle), and so the feature degrades silently if
 * the inspector isn't mounted (e.g. for a non-employee).
 */
function openInInspector(frame: StackFrame): void {
  const filePath = frame.file.startsWith("/") ? frame.file : `/${frame.file}`;
  window.dispatchEvent(
    new CustomEvent("dashboard:open-file", {
      detail: { filePath, componentName: frame.name },
    }),
  );
}

// Tailwind equivalents of the styles this view used to carry inline. The
// reds are the literal values from before — this is a developer-only screen
// with its own palette, not one built from theme tokens.
const DEV_CONTAINER =
  "m-8 rounded-lg border border-red bg-card p-10 shadow-[0_2px_4px_rgba(255,0,0,0.1)]";
const DEV_HEADING = "m-0 mb-[0.2rem] text-[2rem] font-semibold text-red";
const DEV_SUBHEADING = "m-0 mb-8 text-base font-normal text-content-secondary";
const DEV_SECTION_HEADING = "mt-6 mb-2 text-base font-medium text-foreground";
const DEV_PRE =
  "my-2 mb-6 rounded-[4px] bg-very-light-red p-4 font-mono text-[0.9rem] leading-normal break-all whitespace-pre-wrap text-red";
const DEV_RETRY =
  "mt-8 cursor-pointer rounded-[4px] border-0 bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-danger-strong focus:outline-none focus:shadow-[0_0_0_3px_rgba(255,0,0,0.3)]";

const PROD_CONTAINER =
  "flex min-h-[60vh] flex-col items-center justify-center px-8 py-16 text-center";
const PROD_HEADING = "mb-4 text-[2.5rem] font-bold text-foreground";
const PROD_MESSAGE =
  "mx-auto mb-8 max-w-xl text-lg leading-relaxed text-content-secondary";
const PROD_RETRY =
  "cursor-pointer rounded-md border-0 bg-blue px-8 py-3 text-base font-semibold text-white transition-colors hover:bg-info-strong focus:outline-none focus:shadow-[0_0_0_3px_rgba(49,130,206,0.5)]";

/**
 * Fallback for a stack `parseStackFrames` can't link to source — i.e. one
 * from a bundled build. Lists the names only: a bundle's file names and line
 * numbers change on every build and can't be opened by anyone.
 */
function formatStack(stack: string): string {
  if (!stack) return "";

  return stack
    .split("\n")
    .map((line) => {
      const match = line.match(/at\s+(\w+)\s+\((.*?):(\d+):(\d+)\)/);
      if (!match) return undefined;

      const [, component, file] = match;

      if (component === undefined || file === undefined) {
        return undefined;
      }

      if (file.includes("node_modules") || component.startsWith("_")) {
        return undefined;
      }

      return component;
    })
    .filter(Boolean)
    .join("\n");
}

interface ErrorBoundaryViewProps {
  children: ReactNode;
}

interface ErrorBoundaryViewState {
  error: Error | undefined;
  componentStack: string;
}

export class ErrorHandlingBoundaryView extends Component<
  ErrorBoundaryViewProps,
  ErrorBoundaryViewState
> {
  state: ErrorBoundaryViewState = {
    error: undefined,
    componentStack: "",
  };

  static getDerivedStateFromError(
    error: Error,
  ): Partial<ErrorBoundaryViewState> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ componentStack: errorInfo.componentStack ?? "" });
    captureException(error, {
      "error.boundary": "ErrorHandlingBoundaryView",
      "component.stack": errorInfo.componentStack ?? "",
    });
  }

  resetError = (): void => {
    this.setState({ error: undefined, componentStack: "" });
  };

  render(): ReactNode {
    const { error, componentStack } = this.state;

    if (!error) {
      return this.props.children;
    }

    if (isProduction()) {
      return (
        <div role="alert" className={PROD_CONTAINER}>
          <h1 className={PROD_HEADING}>Oops! Something's not quite right</h1>
          <p className={PROD_MESSAGE}>
            We've hit a temporary snag. Our team has been notified and is
            working to get things back on track. Try refreshing the page or come
            back in a few minutes.
          </p>
          <button
            type="button"
            className={PROD_RETRY}
            onClick={this.resetError}
          >
            Try Again
          </button>
        </div>
      );
    }

    const errorFrames = parseStackFrames(error.stack ?? "");
    const componentFrames = parseStackFrames(componentStack);

    return (
      <div role="alert" className={DEV_CONTAINER}>
        <h2 className={DEV_HEADING}>Something went wrong</h2>
        <div className={DEV_SUBHEADING}>
          Please make sure to create a Jam / Linear to report this issue
        </div>

        <h3 className={DEV_SECTION_HEADING}>Error Message:</h3>
        <pre className={DEV_PRE}>{error.message}</pre>

        {errorFrames.length > 0 && (
          <>
            <h3 className={DEV_SECTION_HEADING}>Stack Trace:</h3>
            <StackFrameList
              frames={errorFrames}
              onSelectFrame={openInInspector}
              hint="Click a frame to open it in the Component Inspector (dev-only, FreightSimple employees)."
            />
          </>
        )}
        {errorFrames.length === 0 && error.stack && (
          <>
            <h3 className={DEV_SECTION_HEADING}>Stack Trace:</h3>
            <pre className={DEV_PRE}>{formatStack(error.stack)}</pre>
          </>
        )}

        {componentFrames.length > 0 ? (
          <>
            <h3 className={DEV_SECTION_HEADING}>Component Stack:</h3>
            <StackFrameList
              frames={componentFrames}
              onSelectFrame={openInInspector}
              hint="Click a frame to open it in the Component Inspector (dev-only, FreightSimple employees)."
            />
          </>
        ) : (
          componentStack && (
            <>
              <h3 className={DEV_SECTION_HEADING}>Component Stack:</h3>
              <pre className={DEV_PRE}>{formatStack(componentStack)}</pre>
            </>
          )
        )}

        <button type="button" className={DEV_RETRY} onClick={this.resetError}>
          Try Again
        </button>
      </div>
    );
  }
}
